import React, { useContext, useEffect, useState } from "react";
import { Link } from "gatsby";
import tw from "twin.macro";

import AppContext from "../AppContext";
import Container from "../Container";
import SubNav from "./SubNav";
import LinkToPDF from "@components/LinkToPDF";
import SkipNav from "@components/SkipNav";
import SisBanner from "@components/Header/SisBanner";
import useMobileDetection from "@klickinc/kfs-components-use-mobile-detection";
import useScrollDirection from "@klickinc/kfs-components-use-scroll-direction";

import { lockScroll, resetScroll, scrollToSection } from "@utils/utils";

import logo from "@images/logo.svg";
import menuTrigger from "@images/menu-trigger.svg";
import menuClose from "@images/menu-close.svg";

const Header = ({ nav, location, innerRef }) => {
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const isMobile = useMobileDetection();
  const scrollDirection = useScrollDirection(
    innerRef && innerRef?.current ? innerRef : null,
  );
  const { setShowSignUpModal, setIsHeaderVisible } = useContext(AppContext);

  const linkText = tw`font-black text-[1.125rem] leading-[1.1] tracking-[-0.0225rem]`;

  useEffect(() => {
    if (!isMobile) {
      setMobileNavOpen(false);
    } else {
      if (mobileNavOpen) {
        lockScroll();
      } else {
        resetScroll();
      }
    }
  }, [mobileNavOpen, setMobileNavOpen]);

  const toggleMobileMenu = (e) => {
    e.preventDefault();

    if (!isMobile) return false;

    setMobileNavOpen(!mobileNavOpen);
  };
  return (
    <header
      ref={innerRef}
      css={[
        tw`fixed left-0 top-0 w-full transition-all bg-black bg-opacity-40 lg:(bg-white bg-opacity-100) z-50`,
        scrollDirection === "up" && tw`translate-y-0`,
        scrollDirection === "down" && tw`-translate-y-full`,
        mobileNavOpen &&
        tw`flex flex-col top-0 left-0 right-0 h-screen overflow-scroll max-h-[-webkit-fill-available]`,
      ]}
      onTransitionEnd={() => {
        setIsHeaderVisible(scrollDirection === "down" ? false : true);
      }}
    >
      <SkipNav
        linkToMain="#main-content"
        tw="block h-0 p-0 text-center uppercase opacity-0 bg-white focus:(h-auto opacity-100 py-2) lg:text-lg"
      >
        Skip to main content
      </SkipNav>
      <div tw="bg-deepViolet00 text-white font-bold text-sm leading-[0.78] tracking-[-0.0175rem] pb-2 lg:(pb-[0.625rem])">
        <SisBanner />
        <Container tw="mx-[4%] max-w-[60.25rem]">
          <nav tw="flex flex-col pt-2 lg:(flex-row pt-[0.625rem])">
            <div tw="grow capitalize">
              For U.S. healthcare professionals only
            </div>
            <ul
              data-component="top-nav"
              data-gtm-label="Top Nav"
              tw="flex mt-2 lg:mt-0"
            >
              <li>
                <LinkToPDF url="/pi" fileLocation="header">
                  Prescribing Information
                </LinkToPDF>
              </li>
              <li tw="ml-4 relative before:(absolute -left-2 h-full w-[1px] bg-white)">
                <Link
                  to="#isi"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setMobileNavOpen(false);
                    setTimeout(() => {
                      scrollToSection("#isi", 35);
                    }, 100);
                    if (window.history.pushState) {
                      window.history.pushState(null, null, "#isi");
                    } else {
                      window.location.hash = "#isi";
                    }
                  }}
                >
                  Important Safety Information
                </Link>
              </li>
            </ul>
          </nav>
        </Container>
      </div>
      {/* mobile logo and menu trigger */}
      <Container tw="bg-white mx-0 px-[4%] max-w-none md:(mx-0 px-[6%]) lg:hidden">
        <div
          css={[
            tw`w-full flex flex-row items-center justify-between mx-auto pt-5 pb-[1.1rem]`,
            mobileNavOpen && [tw`grid grid-cols-2`],
          ]}
        >
          <Link
            to="/"
            tw="w-[10.9375rem] [>*]:(pointer-events-none)"
            data-kfs="header-logo"
            aria-label="logo"
          >
            <img
              src={logo}
              width="241"
              alt="XERAVA® (eravacycline) for injection homepage"
              tw="pointer-events-none"
            />
          </Link>
          {isMobile && (
            <button
              data-kfs="mobile-nav-trigger"
              onClick={toggleMobileMenu}
              aria-expanded={!isMobile || mobileNavOpen}
              aria-haspopup="menu"
              css={[tw`h-5 w-5 lg:hidden`, mobileNavOpen && tw`ml-auto`]}
              aria-label="toggle mobile menu"
            >
              <img src={mobileNavOpen ? menuClose : menuTrigger} alt="" />
            </button>
          )}
        </div>
      </Container>
      <div
        css={[
          tw`lg:(flex flex-row items-center justify-between mx-auto max-w-[60.25rem] pt-9 pb-8)`,
        ]}
      >
        <Link
          to="/"
          tw="hidden [>*]:(pointer-events-none) lg:(block max-w-[14.97rem] shrink-0 mr-[2.35rem])"
          data-kfs="header-logo"
          aria-label="logo"
        >
          <img
            src={logo}
            width="241"
            alt="XERAVA® (eravacycline) for injection homepage"
            tw="pointer-events-none"
          />
        </Link>
        <nav
          aria-label="main"
          css={[tw`hidden lg:(block)`, mobileNavOpen && tw`block col-span-2`]}
        >
          <ul
            data-component="main-nav"
            data-gtm-label={isMobile ? "Mobile Menu" : "Main Menu"}
            css={[
              tw`flex grow-0 flex-col lg:(flex-row grow items-end gap-[1.6875rem])`,
              linkText,
              mobileNavOpen && tw`block`,
            ]}
          >
            {nav.map((item, i) => {
              return (
                <li
                  key={`nav-${i}`}
                  css={[
                    tw`bg-deepViolet00 text-grey lg:(bg-transparent text-deepViolet01 max-w-[5.35rem] last-of-type:(max-w-[6rem]))`,
                    location?.pathname === item.url && tw`text-periwinkle01`,
                  ]}
                  className="group"
                >
                  {item.subNav ? (
                    <SubNav
                      buttonTitle={
                        item.title === "Efficacy & Safety" ? (
                          <>Efficacy &&nbsp;Safety</>
                        ) : (
                          item.title
                        )
                      }
                      buttonUrl={item.url}
                      subNavLinks={item.subNav}
                      setMobileNavOpen={setMobileNavOpen}
                      mobileNavOpen={mobileNavOpen}
                    />
                  ) : (
                    <Link
                      to={item.url}
                      tw="flex self-center px-[1.875rem] py-[1.125rem] border-b border-periwinkle01 lg:(px-0 py-2 border-none)"
                    >
                      {item.title}
                    </Link>
                  )}

                  {/* active page styling */}
                  <div
                    css={[
                      tw`hidden lg:(relative after:(absolute w-full h-[0.1875rem] left-0 -top-1 rounded-[4.28444rem] bg-periwinkle01) group-hover:(block))`,
                      location?.pathname === item.url && tw`block`,
                    ]}
                  ></div>
                </li>
              );
            })}
          </ul>
        </nav>
        <div
          data-gtm-label="header-cta"
          css={[
            tw`hidden text-grey lg:(shrink-0 block min-w-[8.125rem] py-0.5 ml-9 -translate-y-1 pl-[0.94rem] border-l-[0.1875rem] border-periwinkle01)`,
            linkText,
            mobileNavOpen && tw`block col-span-2`,
          ]}
        >
          <button
            data-kfs="contact-a-rep-trigger-header"
            css={[
              tw`block w-full max-[1023px]:(bg-deepViolet01 [clip-path: none] text-left px-[1.875rem] py-[1.125rem] border-b border-b-periwinkle01) lg:(text-magenta underline hocus:(text-[#990659]))`,
              linkText,
            ]}
            onClick={() => setShowSignUpModal(true)}
          >
            Contact a Rep
          </button>
          <Link
            onClick={() => setTimeout(setMobileNavOpen(false), 400)}
            to="/access-and-resources#order-xerava"
            css={[
              tw`block max-[1023px]:(bg-deepViolet01 [clip-path: none] text-left px-[1.875rem] py-[1.125rem]) lg:(mt-3.5 text-magenta underline hocus:(text-[#990659]))`,
              linkText,
            ]}
          >
            Ordering Info
          </Link>
        </div>
      </div>
    </header>
  );
};

export default Header;
